import { z } from 'zod'
import { logger } from '@clofo/lib/logger'

/**
 * Contract shared with the backend's `saml_redirect.html` template (and the legacy Ember login
 * app): after the IdP round-trip the popup lands on that backend-rendered page, which writes the
 * session payload — or `{ error }` — to this localStorage key. The write fires a `storage` event
 * in the opener window, which is how the login form learns the SSO flow finished.
 */
export const SAML_SESSION_STORAGE_KEY = 'saml:sessionData'

/**
 * The payload also carries the refresh-token fields on success, but the login form only needs to
 * know whether the flow failed, so everything except `error` is ignored.
 */
const SamlSessionDataSchema = z.object({ error: z.string().optional() })

export type SamlSessionResult = { ok: true } | { ok: false; error: string }

/**
 * Interprets a value written under SAML_SESSION_STORAGE_KEY.
 *
 * Validates with `schema.parse` rather than `parseWithZod`: the latter falls back to the
 * unvalidated payload in production, which would let a malformed payload read as a successful
 * login. This is an auth decision, so it must fail closed in every environment.
 */
export const parseSamlSessionData = (raw: string): SamlSessionResult => {
  try {
    const data = SamlSessionDataSchema.parse(JSON.parse(raw))
    return data.error ? { ok: false, error: data.error } : { ok: true }
  } catch (err) {
    // Not JSON, or not matching the schema. The error message deliberately omits the raw
    // payload — a near-valid one could carry session tokens.
    logger.error(err)
    return { ok: false, error: 'Malformed SAML session payload' }
  }
}
